import { createApi } from '@reduxjs/toolkit/query/react';
import { endpoints } from '../../../helper/endpoints';
import { StatusNumber } from '../../../helper/statusNumber';
import { Quote, QuoteGroupCreateRequest, QuoteGroupResponse } from '../../../shared/types/quotes';
import { RootState } from '../../../store';
import { axiosBaseQuery } from '../axiosBaseQuery';
import { clearError, createError } from '../errorSlice/errorSlice';
import { setCreatingQuote, setQuoteGroup } from '../quoteSlice/quoteSlice';

interface ReferQuoteRequest {
	quote: QuoteGroupResponse;
	files: any[];
	referralReason: string;
	comments: string;
}

// Define a service using a base URL and expected endpoints
export const quoteApi = createApi({
	reducerPath: 'quoteApi',
	tagTypes: ['QUOTES', 'QUOTE'],
	baseQuery: axiosBaseQuery({ baseUrl: '' }),
	endpoints: (builder) => ({
		getQuotesByBrokerId: builder.query<QuoteGroupResponse[], number | string>({
			providesTags: (result, error, args) => [{ type: 'QUOTES', id: 'LIST' }],
			query: (brokerId) => ({
				url: endpoints.GET_QUOTE_GROUPS_BY_BROKER_ID,
				method: 'POST',
				data: brokerId,
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
					'Access-Control-Allow-Credentials': true,
				},
			}),
			transformResponse: (response) => response,
			transformErrorResponse: (response, _, quoteId) => {
				console.log(`Error getting quotes ${quoteId}`, response);
				return [];
			},
			onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
				} catch (error: any) {
					// Handle the error here if necessary
					console.log(`Error in getQuotesByBrokerId`, error);
				} finally {
					dispatch(quoteApi.util.invalidateTags(['QUOTE']));
				}
			},
		}),
		getQuoteGroup: builder.query<QuoteGroupResponse, number>({
			providesTags: (result, error, quoteId) => [{ type: 'QUOTE', id: quoteId }],
			query: (quoteId) => ({
				url: endpoints.GET_QUOTE_GROUP_BY_QUOTE_ID,
				method: 'POST',
				data: quoteId,
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			onQueryStarted: async (quoteId, { getState, dispatch, queryFulfilled }) => {
				// console.log('[QUERY] getQuoteGroup', quoteId);
				try {
					const res = await queryFulfilled;
					dispatch(setQuoteGroup(res.data));
				} catch (error: any) {
					console.log('Error getting quote group', quoteId);
				}
			},
			// transformResponse: (response) => response,
			transformErrorResponse: (response, _, quoteId) => {
				console.log(`Error getting quote group ${quoteId}`, response);
			},
		}),
		createNewQuoteGroup: builder.mutation<QuoteGroupResponse, QuoteGroupCreateRequest>({
			invalidatesTags: [{ type: 'QUOTES', id: 'LIST' }],
			query: (quote) => ({
				url: endpoints.CREATE_QUOTE_GROUP,
				method: 'POST',
				data: JSON.stringify(quote),
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformResponse: (response) => {
				// console.log('RESPONSE OF CREATE QUOTE GROUP', response);
				// localStorage.setItem("quoteId", response.id.toString())
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// console.log('[MUTATION] createQuoteGroup');
				try {
					dispatch(setCreatingQuote(true));
					const res = await queryFulfilled;
					dispatch(clearError());
					dispatch(setQuoteGroup(res.data));
					const patchResult = dispatch(
						quoteApi.util.upsertQueryData('getQuoteGroup', res.data.id, res.data)
					);
				} catch (error: any) {
					dispatch(createError(error));
					console.log('Error creating new quote group', error);
					dispatch(setCreatingQuote(false));
				}
			},
		}),
		updateQuoteGroup: builder.mutation<
			QuoteGroupResponse,
			{
				quote: QuoteGroupResponse | QuoteGroupCreateRequest;
				currentQuote: QuoteGroupResponse;
			}
		>({
			// invalidatesTags: (result, error, { currentQuote }) => [
			// 	{ type: "QUOTE", id: currentQuote.id },
			// 	{ type: "QUOTES", id: "LIST" },
			// ],
			queryFn: async ({ quote, currentQuote }, { dispatch }, _extraOptions, baseQuery) => {
				// console.log('[MUTATION] updateQuoteGroup', quote, currentQuote, JSON.stringify(quote) != JSON.stringify(currentQuote));

				const quotes = currentQuote.quotes;
				let updateBody = { ...quote };

				const isReferred = quotes.some(
					(q) => q.status.statusNumber === StatusNumber.Referred
				);
				const isReferralApproved = quotes.some(
					(q) => q.status.statusNumber === StatusNumber.ReferralApproved
				);
				const isReferralApprovedModifiable = quotes.some(
					(q) => q.status.statusNumber === StatusNumber.ReferralApprovedModifiable
				);
				console.log('Quote is referral approved modifiable?', isReferralApprovedModifiable);

				const notInProgress = quotes.some(
					(q) => q.status.statusNumber !== StatusNumber.QuoteInProgress
				);

				if (!isReferralApproved && notInProgress && !isReferralApprovedModifiable) {
					// && !(isReferred || isReferralApproved)) {
					console.log('Cannot update quote as it is not in progress', currentQuote);
					return { data: currentQuote };
				}

				// const client = {
				// 	...currentQuote.quoteClient,
				// 	address1: quote.quoteClient.address1,
				// 	address2: quote.quoteClient.address2,
				// 	postcode: quote.quoteClient.postcode,
				// 	stateCode: quote.quoteClient.stateCode,
				// };

				// if (isReferred) {
				// 	updateBody = {
				// 		...currentQuote,
				// 		quoteClient: client,
				// 	};
				// } else if (isReferralApproved) {
				// 	updateBody = {
				// 		...currentQuote,
				// 		quoteClient: client,
				// 		quotes: currentQuote.quotes.map((q) => ({
				// 			...q,
				// 			policyPeriodEndDate: quote.quotes[0].policyPeriodEndDate,
				// 			policyPeriodStartDate: quote.quotes[0].policyPeriodStartDate,
				// 		})),
				// 	};
				// } else {
				// 	updateBody = { ...quote };
				// }

				dispatch(clearError());
                console.log('Update quote update body', updateBody);
				const result = await baseQuery({
					url: endpoints.UPDATE_QUOTE_GROUP,
					method: 'POST',
					data: JSON.stringify(updateBody),
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json, text/plain, */*',
					},
				});
				console.log('Update quote result', { currentQuote, quote, result});
				if (result.error) {
					// console.log(`Error updating quote group ${quote.id} in queryFn`, result.error);
					dispatch(createError(result));
					return {
						error: result.error,
					};
				}

				const patchResult = dispatch(
					quoteApi.util.updateQueryData('getQuoteGroup', quote.id, (draft) => {
						Object.assign(draft, result.data);
					})
				);

				// console.log('Update quote res', result.data);
				dispatch(setQuoteGroup(result.data));
				dispatch(
					quoteApi.util.invalidateTags([
						// { type: "QUOTE", id: currentQuote.id },
						{ type: 'QUOTES', id: 'LIST' },
					])
				);
				return result;
			},
		}),
		updateQuoteStatusToReferralApprovedModifiable: builder.mutation<any, number>({
			invalidatesTags: (response, error, quoteId) => [
				{ type: 'QUOTES', id: 'LIST' },
				{ type: 'QUOTE', id: quoteId },
			],
			query: (quoteId) => ({
				url: endpoints.UPDATE_QUOTE_GROUP_STATUS_TO_REFERRAL_APPROVED_MODIFIABLE,
				method: 'POST',
				data: JSON.stringify(quoteId),
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformResponse: (response) => {
				console.log(
					'RESPONSE OF UPDATE_QUOTE_GROUP_STATUS_TO_REFERRAL_APPROVED_MODIFIABLE',
					response
				);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch(clearError());
				} catch (error: any) {
					dispatch(createError(error));
					console.log(
						'Error updating quote group status to Referral Approved Modifiable',
						error
					);
				}
			},
		}),
		bindQuoteGroup: builder.mutation<QuoteGroupResponse, number>({
			invalidatesTags: (response, error, quoteId) => [
				{ type: 'QUOTES', id: 'LIST' },
				{ type: 'QUOTE', id: quoteId },
			],
			query: (quoteId) => ({
				url: endpoints.BIND_QUOTE_GROUP,
				method: 'POST',
				data: JSON.stringify(quoteId),
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformResponse: (response) => {
				// console.log('RESPONSE OF BIND QUOTE GROUP', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					dispatch(clearError());
				} catch (error: any) {
					dispatch(createError(error));
					console.log('Error binding quote group', error);
				}
			},
		}),
		referQuoteGroup: builder.mutation<any, ReferQuoteRequest>({
			queryFn: async (
				{ quote, files, referralReason, comments },
				{ dispatch },
				_extraOptions,
				baseQuery
			) => {
				// Create a new FormData object to prepare the referral data.
				const formData = new FormData();

				const quoteGroupId = quote.id;
				formData.append('quoteGroupId', quoteGroupId.toString());

				// Append the IDs of quotes in the quote group to the referral data.
				// NOTE: For now we pass all quote IDs but future may require only quotes with referral status
				for (let id of quote.quotes.map((q: Quote) => q?.id)) {
					formData.append('referralQuoteIds', id.toString());
				}

				// Append attached files to the referral data.
				for (let file of files) {
					formData.append('files', file);
				}

				// Append referral reason and comments to the referral data.
				formData.append('referralReason', referralReason);
				formData.append('comments', comments);

				const result = await baseQuery({
					url: endpoints.REFER_QUOTE_GROUP,
					method: 'POST',
					data: formData,
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});

				dispatch(clearError());
				// console.log(`Refer quote group ${quoteGroupId}`, result);

				if (result.error) {
					// console.log(`Error referring quote group ${quote.id} in queryFn`, result.error);
					dispatch(createError(result));
					return {
						error: result.error,
					};
				}

				dispatch(
					quoteApi.util.invalidateTags([
						{ type: 'QUOTE', id: quote.id },
						{ type: 'QUOTES', id: 'LIST' },
					])
				);
				return result;
			},
		}),
	}),
});

// Export hooks for usage in functional components
export const {
	useGetQuotesByBrokerIdQuery,
	useCreateNewQuoteGroupMutation,
	useGetQuoteGroupQuery,
	useUpdateQuoteGroupMutation,
	useUpdateQuoteStatusToReferralApprovedModifiableMutation,
	useBindQuoteGroupMutation,
	useReferQuoteGroupMutation,
} = quoteApi;
